import React, { useEffect, useState } from "react";
import { ModalX, PageHeader } from "../../components";
import { Button, Checkbox, Chip, IconButton, Input, Option, Select, Spinner, Typography } from "@material-tailwind/react";
import { ArrowDownIcon, ArrowUpIcon, Edit2Icon, UserIcon, XIcon } from "lucide-react";
import moment from "moment";
import { convertToPresentableText, getOrdinal, NotifyError } from "../../utils/functions";
import { LEVELS, LEVELS_ENUM, OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM, SECURITY_TYPES, SECURITY_TYPES_ENUM } from "../../utils/enums";
import { Link } from "react-router-dom";
import { useAddOffice, useGetOffices, useUpdateOffice } from "../../custom-hooks/use-offices";
import { useGetFloors } from "../../custom-hooks/use-floors";
import { useConfirm } from "../../custom-hooks/confirm-dialog";
import { useDeleteBooking } from "../../custom-hooks/use-bookings";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import { usePermissions } from "../../custom-hooks/use-permissions";


const INITIAL_FORM_DATA = {
    floor: '',
    number: '',
    code: '',
    capacity: '',
    useableArea: '',
    totalArea: '',
    status: '',
    securityType: '',
    securityLevel: '',
    isAccessible: true,
    isBookable: true,
    dedicatedClimateControl: true,
    dedicatedInternet: false,
    hourlyRate: 0,
    dailyRate: 0,
    monthlyRate: 0,
    currency: 'PKR',
}


const OfficePage = () => {

    // State variables
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedOfficeId, setSelectedOfficeId] = useState(null);
    // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
    // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
    const [sort, setSort] = useState('-createdDate');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [filterStatus, setFilterStatus] = useState(OPERATIONAL_STATUSES.OPERATIONAL);
    const [filterCapacity, setFilterCapacity] = useState('');


    useEffect(() => {
        setIsTyping(true);
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setIsTyping(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const addOfficeMutation = useAddOffice();
    const updateOfficeMutation = useUpdateOffice();

    const { data: offices, isLoading: isOfficesLoading, isError: isOfficesError } = useGetOffices('all', {
        // _page: page,
        // _limit: limit,
        _sort: sort,
        _search: debouncedSearch,
        status: filterStatus,
        capacity: filterCapacity === 'All' ? undefined : filterCapacity,
        // ...filters
    });

    const { data: floors, isLoading: isFloorsLoading, isError: isFloorsError } = useGetFloors();

    const { can } = usePermissions();
    const { confirm, ConfirmationDialog } = useConfirm();
    const deleteBookingMutation = useDeleteBooking();


    // Handle form input change
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckBoxChange = async (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (isUpdating && formData.status === OPERATIONAL_STATUSES.CLOSED && formData.bookings && formData.bookings.length > 0) {
            const isConfirmed = await confirm({
                title: "Cancel Active Bookings",
                message: `This office has ${formData.bookings.length} active booking(s). Closing it will cancel the booking(s). Do you want to proceed?`,
                confirmText: "Yes, Cancel Bookings",
                confirmBtnColor: "red"
            });
            if (isConfirmed) {
                try {
                    for (const booking of formData.bookings) {
                        await deleteBookingMutation.mutateAsync(booking._id);
                    }
                } catch (error) {
                    NotifyError("Failed to cancel bookings", error);
                    return;
                }
            } else {
                return;
            }
        }

        try {
            if (isUpdating) {
                // Update building logic
                const updatedOffice = {
                    floor: formData.floor,
                    number: formData.number,
                    code: formData.code,
                    capacity: formData.capacity,
                    useableArea: formData.useableArea,
                    totalArea: formData.totalArea,
                    status: formData.status,
                    securityType: formData.securityType,
                    securityLevel: formData.securityLevel,
                    isAccessible: formData.isAccessible,
                    isBookable: formData.isBookable,
                    dedicatedClimateControl: formData.dedicatedClimateControl,
                    dedicatedInternet: formData.dedicatedInternet,
                    hourlyRate: formData.hourlyRate,
                    dailyRate: formData.dailyRate,
                    monthlyRate: formData.monthlyRate,
                };
                await updateOfficeMutation.mutateAsync({ officeId: selectedOfficeId, data: updatedOffice });
            } else {
                // Add new building logic
                const newOffice = {
                    floor: formData.floor,
                    number: formData.number,
                    code: formData.code,
                    capacity: formData.capacity,
                    useableArea: formData.useableArea,
                    totalArea: formData.totalArea,
                    status: formData.status,
                    securityType: formData.securityType,
                    securityLevel: formData.securityLevel,
                    isAccessible: formData.isAccessible,
                    isBookable: formData.isBookable,
                    dedicatedClimateControl: formData.dedicatedClimateControl,
                    dedicatedInternet: formData.dedicatedInternet,
                    hourlyRate: formData.hourlyRate,
                    dailyRate: formData.dailyRate,
                    monthlyRate: formData.monthlyRate,
                };
                await addOfficeMutation.mutateAsync({ floorId: formData.floor, data: newOffice });

            }
        } catch (error) {
            console.log('error', error);
            NotifyError(error.response.data.message, error);
        } finally {
            setModalOpen(false);
            setFormData(INITIAL_FORM_DATA);
            setIsUpdating(false);
            setSelectedOfficeId(null);
        }
    };

    // Open modal for adding a new building
    const openAddOfficeModal = () => {
        setModalOpen(true);
        const defaultValueForInitialFormData = { ...INITIAL_FORM_DATA };
        defaultValueForInitialFormData.status = OPERATIONAL_STATUSES.OPERATIONAL;
        defaultValueForInitialFormData.securityType = SECURITY_TYPES.SHARED_FINGERPRINT;
        defaultValueForInitialFormData.securityLevel = LEVELS.MEDIUM;

        setFormData(defaultValueForInitialFormData);
        setIsUpdating(false);
    };

    // Open modal for updating a building
    const openUpdateOfficeModal = (office) => {
        setModalOpen(true);
        setFormData({ ...office, floor: office.floor._id });
        setIsUpdating(true);
        setSelectedOfficeId(office._id);
    };

    const interpretOccupency = (occupency) => {
        if (!occupency) {
            return <Chip color="green" size="sm" className="text-xs" variant="ghost" value={`Full time`} />;
        }
        if (occupency.isFullyOccupied) {
            return <Chip color="red" size="sm" className="text-xs" variant="ghost" value={`Occupied`} />;
        }
        return <Chip color="blue" size="sm" className="text-xs" variant="ghost" value={`${occupency.occupiedHours} hours`} />;
    }

    const TABLE_COLUMNS = [
        {
            label: 'Office #',
            key: 'code',
            sortable: true,
            sort: sort === "code" ? "-code" : "code"
        },
        {
            label: 'Floor',
            key: 'floor.floorCode',
            sortable: true,
            sort: sort === "floor.floorCode" ? "-floor.floorCode" : "floor.floorCode"
        },
        {
            label: 'Capacity',
            key: 'capacity',
            sortable: true,
            sort: sort === "capacity" ? "-capacity" : "capacity"
        },
        {
            label: 'Availability',
            key: 'availability',
            sortable: false,
            sort: null
        },
        {
            label: 'Curr Assig.',
            key: 'currentAssignment',
            sortable: false,
            sort: null
        },
        {
            label: 'Timestamp',
            key: 'createdDate',
            sortable: true,
            sort: sort === "createdDate" ? "-createdDate" : "createdDate"
        },
        {
            label: 'Actions',
            key: 'actions',
            sortable: false,
            sort: null,
            hidden: !can('FX7', 'edit')
        }
    ].filter(col => !col.hidden);

    const availableCapacities = ['All', ...Array.from({ length: 20 }, (_, i) => (i + 1).toString())];

    return (
        <div className={``}>
            <div className="m-5 p-5 border rounded-3xl">
                <PageHeader
                    title="Office Management"
                    isLoading={isOfficesLoading}
                    actionOnClick={can('FX7', 'edit') ? openAddOfficeModal : undefined}
                    actionType="add"
                    actionText="Add New Office"
                />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
                    <Input
                        label="Search Offices"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        icon={isTyping ? <Spinner className="h-4 w-4" /> : <XIcon className={searchTerm ? "cursor-pointer text-gray-500" : "hidden"} size={16} onClick={() => setSearchTerm('')} />}
                    />
                    <Select
                        label="Filter by Capacity"
                        value={filterCapacity}
                        onChange={(value) => setFilterCapacity(value)}
                    >
                        {
                            availableCapacities.map((capacity, index) => (
                                <Option key={index} value={capacity}>{capacity}</Option>
                            ))
                        }
                    </Select>
                    <Select
                        label="Filter by Status"
                        value={filterStatus}
                        onChange={(value) => setFilterStatus(value)}
                    >
                        {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                            <Option key={index} value={status}>{convertToPresentableText(status)}</Option>
                        ))}
                    </Select>
                </div>
                <div className="overflow-x-auto">
                    {isOfficesLoading ? (
                        <div className="flex justify-center py-5">
                            <Spinner />
                        </div>
                    ) : isOfficesError ? (
                        <div className="text-center py-5 text-red-500">
                            Error loading offices. Please try again.
                        </div>
                    ) : (
                        <table className="w-full min-w-max table-auto text-left">
                            <thead className="text-sm">
                                <tr className="py-3">
                                    <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Offices ({offices?.length})</th>
                                </tr>
                                <tr className="py-3 capitalize">
                                    {
                                        TABLE_COLUMNS.map((column, index) => (
                                            <th
                                                key={index}
                                                className={"cursor-pointer py-3"}
                                                onClick={() => setSort(column.sortable ? column.sort : sort)}
                                            >
                                                <Typography
                                                    variant="medium"
                                                    color="blue-gray"
                                                    className="flex items-center gap-2 font-bold leading-none opacity-70">
                                                    {column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
                                                </Typography>
                                            </th>
                                        ))
                                    }

                                </tr>
                            </thead>
                            <tbody>
                                {offices?.map((office, index) => (
                                    <tr
                                        key={index}
                                        className={`border-t hover:bg-gray-50 button rounded-full ${office.status === OPERATIONAL_STATUSES.CLOSED ? 'text-gray-500' : ''}`}>
                                        <td className="py-3 text-sm">
                                            <div className="flex gap-1 font-bold">
                                                <span className="text-xs">{office.number}</span>
                                                <span className="text-xs">({office.code})</span>
                                            </div>
                                            <div className="text-xs">
                                                {InterpretOperationalStatus(office.status, "text")}
                                            </div>
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            <div className="font-bold text-sm">
                                                {office.floor.floorCode}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {office.floor.building.name} - {office.floor.building.branch.name}
                                            </div>
                                        </td>
                                        <td className="font-bold py-3 text-lg">
                                            {office.capacity}
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            {
                                                office.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                                    <div className="flex">
                                                        {interpretOccupency(office.occupency)}
                                                    </div>
                                                ) : (
                                                    <div className="flex">
                                                        {InterpretOperationalStatus(office.status)}
                                                    </div>
                                                )
                                            }
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            {
                                                office.currentBookingUsers && office.currentBookingUsers.length > 0 ? (
                                                    office.currentBookingUsers.map(user => (
                                                        <div className="flex gap-1" key={user._id}>
                                                            <Link className="text-xs text-blue-600 hover:text-orange-600 duration-300 flex items-center gap-1" to={`/customer/${user._id}`} target="_blank">
                                                                <UserIcon size={12} /> {user.name}
                                                                {/* <SquareArrowOutUpRightIcon size={12} /> */}
                                                            </Link>
                                                        </div>
                                                    ))
                                                ) :
                                                    (
                                                        office.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                                            <div className="flex">
                                                                <Chip color="green" size="sm" className="text-xs" variant="ghost" value="Available" />
                                                            </div>
                                                        ) : (
                                                            <div className="flex">
                                                                {InterpretOperationalStatus(office.status)}
                                                            </div>
                                                        )
                                                    )

                                            }
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            <div className="flex flex-col">
                                                <div className="text-xs">C: {moment(office.createdDate).format('lll')}</div>
                                                <div className="text-xs">U: {moment(office.lastUpdated).format('lll')}</div>
                                            </div>
                                        </td>
                                        {can('FX7', 'edit') && (
                                        <td className="flex gap-1 py-3">
                                            {/* <IconButton
                                            size="sm"
                                            className="rounded-full"
                                            onClick={() => openUpdateOfficeModal(office)}
                                        >
                                            <EyeIcon size={12} />
                                        </IconButton> */}
                                            <IconButton
                                                size="sm"
                                                color="orange"
                                                className="rounded-full"
                                                onClick={() => openUpdateOfficeModal(office)}
                                            >
                                                <Edit2Icon size={12} />
                                            </IconButton>
                                        </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    )}
                </div>

                {/* Modal for Add/Update Office */}
                <ModalX isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={isUpdating ? "Update Office" : "Add New Office"}
                    maxWidth="xxl">
                    <form onSubmit={handleFormSubmit}>
                        <div className="space-y-2">
                            <div >
                                {
                                    isFloorsError && <div className="text-center py-5 text-red-500">Error loading floors. Please try again.</div>
                                }
                                <Select
                                    label={<>Select Floor <span className="text-red-500 ml-1">*</span></>}
                                    value={formData.floor}
                                    onChange={(newValue) => handleSelectChange("floor", newValue)}
                                    disabled={isFloorsLoading}
                                >
                                    {
                                        floors?.map((floor, index) => (
                                            <Option key={index} value={floor._id}>
                                                <div className="flex justify-between gap-3 w-full">
                                                    <div className="">
                                                        {getOrdinal(floor.number)} Floor - {floor.floorCode} in ({floor.building.name})
                                                    </div>
                                                    <div className="">
                                                        {floor.building.branch.name}
                                                    </div>
                                                </div>
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Input
                                    label="Office Number"
                                    name="number"
                                    value={formData.number}
                                    onChange={handleInputChange}
                                    required
                                />
                                <Input
                                    label="Office Code"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className={`mt-5`}>
                                <span className="font-thin text-lg">Specifications</span>
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Input
                                    label="Person Capacity"
                                    name="capacity"
                                    type="number"
                                    required
                                    value={formData.capacity}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Usable Area (sq/ft)"
                                    name="useableArea"
                                    type="number"
                                    value={formData.useableArea}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Total Area (sq/ft)"
                                    name="totalArea"
                                    type="number"
                                    value={formData.totalArea}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="flex gap-2 flex-col md:flex-row">
                                <Select
                                    label="Status"
                                    value={formData.status}
                                    onChange={newValue => handleSelectChange("status", newValue)}
                                >
                                    {
                                        OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                                            <Option key={index} value={status}>
                                                {convertToPresentableText(status)}
                                            </Option>
                                        ))

                                    }
                                </Select>
                                <Select
                                    label="Security Level"
                                    value={formData.securityLevel}
                                    onChange={newValue => handleSelectChange("securityLevel", newValue)}
                                >
                                    {
                                        LEVELS_ENUM.map((level, index) => (
                                            <Option key={index} value={level}>
                                                {convertToPresentableText(level)}
                                            </Option>
                                        ))
                                    }
                                </Select>
                                <Select
                                    label="Security Type"
                                    value={formData.securityType}
                                    onChange={newValue => handleSelectChange("securityType", newValue)}
                                >
                                    {
                                        SECURITY_TYPES_ENUM.map((type, index) => (
                                            <Option key={index} value={type}>
                                                {convertToPresentableText(type)}
                                            </Option>
                                        ))

                                    }
                                </Select>
                            </div>

                            {/* <div className="">
                                    <span className="text-md font-thin text-gray-900">
                                    Location
                                    </span>
                                </div> */}
                            {/* <div className="flex gap-4">
                                    <Input
                                        label="Google Maps Link"
                                        name="googleMapsLink"
                                        value={formData.googleMapsLink}
                                        onChange={handleInputChange}
                                        icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                                    />

                                    <Input
                                        label="Longitude"
                                        name="lng"
                                        value={formData.lng}
                                        onChange={handleInputChange}
                                        icon={isLoadingCoordinates ? <Spinner /> : <></>}
                                    />
                                    <Input
                                        label="Latitude"
                                        name="lat"
                                        value={formData.lat}
                                        onChange={handleInputChange}
                                        type="number"
                                        icon={isLoadingCoordinates ? <Spinner /> : <></>}
                                    />
                                </div> */}

                            <div className="">
                                <Checkbox
                                    name="isBookable"
                                    checked={formData.isBookable}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Is Bookable"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="isAccessible"
                                    checked={formData.isAccessible}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Is Accessible"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="dedicatedInternet"
                                    checked={formData.dedicatedInternet}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Dedicated Internet"
                                    onChange={handleCheckBoxChange}
                                />
                                <Checkbox
                                    name="dedicatedClimateControl"
                                    checked={formData.dedicatedClimateControl}
                                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                                    label="Dedicated Climate Control"
                                    onChange={handleCheckBoxChange}
                                />
                            </div>
                            <div className={`mt-5`}>
                                <span className="font-thin text-lg">Pricing</span>
                            </div>
                            <div className={`flex gap-4`}>
                                <Input
                                    label="Hourly Rent"
                                    name="hourlyRate"
                                    type="number"
                                    value={formData.hourlyRate}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Daily Rent"
                                    name="dailyRate"
                                    type="number"
                                    value={formData.dailyRate}
                                    onChange={handleInputChange}
                                />
                                <Input
                                    label="Monthly Rent"
                                    name="monthlyRate"
                                    type="number"
                                    value={formData.monthlyRate}
                                    onChange={handleInputChange}
                                />

                            </div>
                        </div>
                        <div className="mt-4 flex justify-end space-x-2">
                            <Button
                                onClick={() => setModalOpen(false)}
                                className="rounded-full "
                                variant="text"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="rounded-full"
                                disabled={addOfficeMutation.isLoading || updateOfficeMutation.isLoading}
                                loading={addOfficeMutation.isLoading || updateOfficeMutation.isLoading}>
                                {isUpdating ? "Update" : "Add"}
                            </Button>
                        </div>
                    </form>
                </ModalX>
            </div>
            <ConfirmationDialog />
        </div>
    );
};

export default OfficePage;
